// src/pages/controlhorario/PanelSucursales.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import {
    Box, Typography, CircularProgress, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, Table, TableHead, TableRow,
    TableCell, TableBody, TextField, Snackbar, Alert, Chip, IconButton,
    Switch, FormControlLabel
} from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import TableContainer from "@mui/material/TableContainer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TodayIcon from "@mui/icons-material/Today";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import BranchCard from "./BranchCard";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import useMqttSubscription from "../../Hooks/useMqttSubscription";
import { logEventos } from "../../services/services";
import "@fontsource/orbitron/600.css";

/* ---------------- helpers ---------------- */
function toYMD(date = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function parseTS(ts) {
    if (!ts) return null;
    const d = typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
}
function ensureYMD(v) {
    if (!v) return toYMD(new Date());
    if (v instanceof Date) return toYMD(v);
    if (typeof v === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        const d = parseTS(v);
        return d ? toYMD(d) : toYMD(new Date());
    }
    const d = parseTS(v);
    return d ? toYMD(d) : toYMD(new Date());
}
function fmtTime(ts) {
    const d = parseTS(ts);
    if (!d) return "—";
    return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(d);
}
function isToday(ymd) { return ymd === toYMD(new Date()); }
function formatLongDateTitle(ymd) {
    const safe = ensureYMD(ymd);
    const [y, m, d] = safe.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    let s = new Intl.DateTimeFormat("es-AR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(date);
    s = s.replace(/,/g, "");
    return s.split(" ").map((w) => (w === "de" || w === "del" ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
}

const api = {
    async getResumenSucursales(date) {
        const r = await fetch(`${UrlApi}/controlhorario/sucursales/summary?date=${encodeURIComponent(date)}`, { credentials: "include" });
        if (!r.ok) throw new Error("No se pudo obtener el resumen.");
        return r.json();
    },
    async getResumenSucursal(sucursalId, date) {
        const r = await fetch(`${UrlApi}/controlhorario/sucursales/${encodeURIComponent(sucursalId)}/summary?date=${encodeURIComponent(date)}`, { credentials: "include" });
        if (!r.ok) throw new Error("no_sucursal_endpoint");
        return r.json();
    },
    async getEmpresaSummary(date) {
        const r = await fetch(`${UrlApi}/controlhorario/empresa/summary?date=${encodeURIComponent(date)}`, { credentials: "include" });
        if (!r.ok) throw new Error("No se pudo obtener el resumen empresa.");
        return r.json();
    },
    async getEmpresaAusentes(date) {
        const r = await fetch(`${UrlApi}/controlhorario/empresa/ausentes?date=${encodeURIComponent(date)}`, { credentials: "include" });
        if (!r.ok) throw new Error("No se pudo obtener ausentes empresa.");
        return r.json();
    },
    async getLegajosPorTipo(sucursalId, tipo, date) {
        const r = await fetch(
            `${UrlApi}/controlhorario/sucursales/${sucursalId}/legajos?tipo=${encodeURIComponent(tipo)}&date=${encodeURIComponent(date)}`,
            { credentials: "include" }
        );
        if (!r.ok) throw new Error("No se pudo obtener el detalle.");
        return r.json();
    },
    async postObservacion(dni, observacion, date, user) {
        const r = await fetch(`${UrlApi}/controlhorario/legajos/${encodeURIComponent(dni)}/observacion`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ observacion, date, user }),
        });
        if (!r.ok) throw new Error("No se pudo guardar la observación.");
        return r.json();
    },
    async getFichadasMensuales(sucursalId, ym) {
        const r = await fetch(
            `${UrlApi}/controlhorario/sucursales/${sucursalId}/mensual/fichadas?ym=${encodeURIComponent(ym)}`,
            { credentials: "include" }
        );
        if (!r.ok) throw new Error("No se pudo obtener fichadas mensuales.");
        return r.json();
    },
    async downloadMarcaciones(empresas, nombreArchivo, fechaDesde, fechaHasta) {
        const params = new URLSearchParams({
            fecha_desde: fechaDesde,
            fecha_hasta: fechaHasta,
            empresas: empresas.join(','),
            nombre_archivo: nombreArchivo
        });
        const url = `${UrlApi}/controlhorario/reportes/marcaciones/download?${params}`;
        window.open(url, '_blank');
    },
};

/* ---------------- main ---------------- */
export default function PanelSucursales() {
    const [selectedDate, setSelectedDate] = useState(toYMD());
    const [loading, setLoading] = useState(true);
    const [resumen, setResumen] = useState([]); // solo sucursales (sin EMP)
    const [empresaTotals, setEmpresaTotals] = useState({ presentes: 0, ausentes: 0, total: 0 });
    const [error, setError] = useState("");

    const [sortBy, setSortBy] = useState(() => {
        const saved = localStorage.getItem("ph_sortBy");
        return saved === "nombre" ? "nombre" : "id";
    });

    // 🔄 Nuevo: preferencia "Solo con presentes" (ON por defecto)
    const [onlyWithPresent, setOnlyWithPresent] = useState(() => {
        const saved = localStorage.getItem("ph_only_present");
        return saved === null ? true : saved === "1";
    });

    // Modal sucursal
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTipo, setModalTipo] = useState("presentes");
    const [modalSucursal, setModalSucursal] = useState(null);
    const [modalRows, setModalRows] = useState([]);
    const [modalDate, setModalDate] = useState(toYMD());

    // Modal descarga marcaciones
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [downloadFechaDesde, setDownloadFechaDesde] = useState(toYMD());
    const [downloadFechaHasta, setDownloadFechaHasta] = useState(toYMD());

    // Modal empresa ausentes
    const [empresaModalOpen, setEmpresaModalOpen] = useState(false);
    const [empresaModalRows, setEmpresaModalRows] = useState([]);

    const [snack, setSnack] = useState({ open: false, text: "", severity: "success" });

    // Reportes
    const [reportOpen, setReportOpen] = useState(false);
    const [reportType, setReportType] = useState(null);
    const [reportSucursal, setReportSucursal] = useState(null);
    const [reportMonth, setReportMonth] = useState(() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; // YYYY-MM
    });
    const [reportRows, setReportRows] = useState([]);

    const currentUser = useMemo(() => localStorage.getItem("user") || "desconocido", []);
    const autoSaveTimersRef = useRef({});
    const mqttRefreshRef = useRef(null);

    // ---- permisos ----
    const isAdmin = useMemo(() => (localStorage.getItem("adminrrhh") === "1"), []);
    const allowedSucSet = useMemo(() => {
        if (isAdmin) return null; // admin ve todo
        const raw = (localStorage.getItem("suc_auth") || "").trim();
        if (!raw) return new Set(); // sin permisos => nada
        return new Set(
            raw.split(",").map(s => s.trim()).filter(Boolean).map(s => String(parseInt(s, 10)))
        );
    }, [isAdmin]);

    useEffect(() => {
        logEventos('Control horario', 'RRHH', 'Ingreso panel control horario', localStorage.getItem('user'));
    }, [])

    useEffect(() => () => {
        Object.values(autoSaveTimersRef.current).forEach((t) => clearTimeout(t));
        clearTimeout(mqttRefreshRef.current);
    }, []);

    useEffect(() => {
        async function loadReport() {
            if (!reportOpen || !reportSucursal || !reportType) return;
            try {
                if (reportType === "mensual_fichadas") {
                    const rows = await api.getFichadasMensuales(reportSucursal.sucursal_id, reportMonth);
                    setReportRows(rows || []);
                } else {
                    setReportRows([]);
                }
            } catch (e) {
                setSnack({ open: true, text: e.message || "No se pudo cargar el reporte.", severity: "error" });
            }
        }
        loadReport();
    }, [reportOpen, reportSucursal, reportType, reportMonth]);

    const scheduleAutoSave = (dni, text, dateArg = selectedDate) => {
        const prev = autoSaveTimersRef.current[dni];
        if (prev) clearTimeout(prev);
        autoSaveTimersRef.current[dni] = setTimeout(async () => {
            try {
                await api.postObservacion(dni, text ?? "", dateArg, currentUser);
                setModalRows((prevRows) => prevRows.map((r) => (r.dni === dni ? { ...r, observacion: text ?? "" } : r)));
            } finally {
                delete autoSaveTimersRef.current[dni];
            }
        }, 600);
    };



    function sortSucursales(list, mode) {
        const copy = [...list];
        if (mode === "nombre") {
            copy.sort((a, b) => String(a.sucursal_nombre || "").localeCompare(String(b.sucursal_nombre || ""), "es", { sensitivity: "base" }));
        } else {
            copy.sort((a, b) => {
                const ai = parseInt(a.sucursal_id, 10);
                const bi = parseInt(b.sucursal_id, 10);
                if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;
                return String(a.sucursal_id).localeCompare(String(b.sucursal_id), "es", { sensitivity: "base" });
            });
        }
        return copy;
    }

    const loadSummary = useCallback(async (date) => {
        setLoading(true);
        try {
            const [sucData, empData] = await Promise.all([
                api.getResumenSucursales(date),
                api.getEmpresaSummary(date),
            ]);

            const normSuc = (sucData || []).map((d) => ({
                ...d,
                ausentes: typeof d.ausentes === "number" ? d.ausentes : Math.max(0, Number(d.total || 0) - Number(d.presentes || 0)),
            }));

            // filtrar por permisos si NO es admin
            const filtered = isAdmin
                ? normSuc
                : normSuc.filter((d) => allowedSucSet?.has(String(d.sucursal_id)));

            const sorted = sortSucursales(filtered, sortBy);

            // Totales empresa (se muestran solo si es admin)
            setEmpresaTotals({
                presentes: Number(empData?.presentes ?? 0),
                ausentes: Number(empData?.ausentes ?? 0),
                total: Number(empData?.total ?? 0),
            });

            setResumen(sorted);
            setError("");
        } catch (e) {
            setError(e.message || "Error al cargar.");
        } finally {
            setLoading(false);
        }
    }, [sortBy, isAdmin, allowedSucSet]);

    useEffect(() => { loadSummary(selectedDate); }, [selectedDate, sortBy, loadSummary]);

    const setHoy = () => setSelectedDate(toYMD(new Date()));
    const setAyer = () => { const d = new Date(); d.setDate(d.getDate() - 1); setSelectedDate(toYMD(d)); };

    async function abrirModal(sucursalObj, tipo, dateOverride) {
        const dateToUse = ensureYMD(dateOverride || selectedDate);

        if (sucursalObj?._empresa === true) return; // ya no existe en el grid

        // Modal de sucursal
        try {
            setModalTipo(tipo);
            setModalSucursal(sucursalObj);
            setModalRows([]);
            setModalDate(dateToUse);
            setModalOpen(true);

            const rows = await api.getLegajosPorTipo(sucursalObj.sucursal_id, tipo, dateToUse);
            setModalRows((rows || []).map((r) => ({ ...r, _obsDraft: r.observacion || "" })));
        } catch (e) {
            setSnack({ open: true, text: e.message || "No se pudo obtener el detalle.", severity: "error" });
        }
    }

    /* ---------- Suscripción MQTT ---------- */
    const recomputeEmpresaTotals = useCallback((list) => {
        const sum = list.reduce((acc, s) => {
            acc.presentes += Number(s.presentes || 0);
            acc.total += Number(s.total || 0);
            return acc;
        }, { presentes: 0, total: 0 });
        setEmpresaTotals({ presentes: sum.presentes, total: sum.total, ausentes: Math.max(0, sum.total - sum.presentes) });
    }, []);

    const onMqttMessage = useCallback(async (payload) => {
        const sid = payload?.sucursalId ?? payload?.sucursal_id ?? null;

        // 0) sin sucursal -> refetch global
        if (sid == null) {
            clearTimeout(mqttRefreshRef.current);
            mqttRefreshRef.current = setTimeout(() => {
                loadSummary(ensureYMD(selectedDate));
            }, 400);
            return;
        }

        // 1) si ya vienen contadores, parche local y recompute empresa
        const hasCounts = ["presentes", "ausentes", "total"].some((k) => typeof payload?.[k] === "number");
        if (hasCounts) {
            setResumen((prev) => {
                const next = prev.map((s) =>
                    String(s.sucursal_id) === String(sid)
                        ? {
                            ...s,
                            presentes: typeof payload.presentes === "number" ? payload.presentes : s.presentes,
                            ausentes: typeof payload.ausentes === "number" ? payload.ausentes : s.ausentes,
                            total: typeof payload.total === "number" ? payload.total : s.total,
                        }
                        : s
                );
                recomputeEmpresaTotals(next);
                return next;
            });
            return;
        }

        // 2) si vino solo sucursalId -> pedir solo esa card y recompute
        try {
            const item = await api.getResumenSucursal(sid, ensureYMD(selectedDate));
            setResumen((prev) => {
                const idx = prev.findIndex((s) => String(s.sucursal_id) === String(sid));
                if (idx === -1) return prev;
                const copy = [...prev];
                copy[idx] = {
                    ...copy[idx],
                    presentes: Number(item.presentes ?? copy[idx].presentes),
                    ausentes: Number(item.ausentes ?? copy[idx].ausentes),
                    total: Number(item.total ?? copy[idx].total),
                };
                recomputeEmpresaTotals(copy);
                return copy;
            });
        } catch {
            clearTimeout(mqttRefreshRef.current);
            mqttRefreshRef.current = setTimeout(() => {
                loadSummary(ensureYMD(selectedDate));
            }, 300);
        }
    }, [loadSummary, selectedDate, recomputeEmpresaTotals]);

    const { connectionStatus, subscribed, toggle } =
        useMqttSubscription("netizar/panelHRcontrol", onMqttMessage);

    /* ---------- Modal responsive ---------- */
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const ModalLegajos = useMemo(() => {
        return function ModalLegajosInner({ open, onClose, sucursal, tipo, rows, date, onDraftChange, onBlurAutoSave }) {
            const title = useMemo(() => {
                if (!sucursal) return "";
                const mapa = { presentes: "Presentes", ausentes: "Ausentes", total: "Total" };
                const fechaStr = `${formatLongDateTitle(date)}${isToday(date) ? " (hoy)" : ""}`;
                return `${sucursal.sucursal_nombre} — ${mapa[tipo] || tipo} — ${fechaStr}`;
            }, [sucursal, tipo, date]);

            return (
                <Dialog
                    open={open}
                    onClose={onClose}
                    fullWidth
                    fullScreen={fullScreen}
                    maxWidth="xl"
                    keepMounted
                    sx={{ zIndex: (t) => t.zIndex.modal + 3, pointerEvents: "auto" }}
                    BackdropProps={{ invisible: true, sx: { pointerEvents: "none" } }}
                    PaperProps={{
                        sx: {
                            width: "100%",
                            height: fullScreen ? "100vh" : { xs: "90vh", sm: "70vh" },
                            m: 0,
                            borderRadius: fullScreen ? 0 : 3,
                            display: "flex",
                            boxShadow: 12,
                        },
                    }}
                >
                    <DialogTitle sx={{ pb: 1.25 }}>{title}</DialogTitle>
                    <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column" }}>
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                Escribí la observación del día; se guarda automáticamente al dejar de tipear.
                            </Typography>
                        </Box>

                        <Box sx={{ px: 1, flex: 1, overflow: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                sx={{
                                    minWidth: 1100,
                                    "& .MuiTableCell-root": { py: 0.4, px: 0.6, fontSize: { xs: 12.5, sm: 13 } },
                                    "& .MuiTableRow-root": { height: "unset" },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 96 }}>Legajo</TableCell>
                                        <TableCell sx={{ width: 240 }}>Apellido y Nombre</TableCell>
                                        <TableCell sx={{ width: 120, display: { xs: "none", sm: "table-cell" } }}>Puesto</TableCell>
                                        <TableCell sx={{ width: 170 }}>T1 Entrada</TableCell>
                                        <TableCell sx={{ width: 170 }}>T1 Salida</TableCell>
                                        <TableCell sx={{ width: 90 }}>T1 Horas</TableCell>
                                        <TableCell sx={{ width: 170, display: { xs: "none", md: "table-cell" } }}>T2 Entrada</TableCell>
                                        <TableCell sx={{ width: 170, display: { xs: "none", md: "table-cell" } }}>T2 Salida</TableCell>
                                        <TableCell sx={{ width: 90, display: { xs: "none", md: "table-cell" } }}>T2 Horas</TableCell>
                                        <TableCell sx={{ width: 90 }}>Total</TableCell>
                                        <TableCell>Observación (día)</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.map((r) => {
                                        const mapLink = (lat, lng) => (lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null);
                                        const t1InLink = mapLink(r.t1_entrada_lat, r.t1_entrada_lng);
                                        const t1OutLink = mapLink(r.t1_salida_lat, r.t1_salida_lng);
                                        const t2InLink = mapLink(r.t2_entrada_lat, r.t2_entrada_lng);
                                        const t2OutLink = mapLink(r.t2_salida_lat, r.t2_salida_lng);

                                        return (
                                            <TableRow key={`leg-${r.legajo}`} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                        {r.legajo ?? "—"}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: r.dni ? "text.secondary" : "error.main", lineHeight: 1.1 }}>
                                                        {r.dni || "— (falta DNI)"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                        {r.apellido ?? "—"}, {r.nombre ?? "—"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap", display: { xs: "none", sm: "table-cell" } }}>
                                                    {r.puesto ?? "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {r.t1_entrada ? (
                                                        <>
                                                            {t1InLink ? (
                                                                <Typography component="a" href={t1InLink} target="_blank" rel="noopener noreferrer"
                                                                    sx={{ color: "#1976d2", textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                                                                    {fmtTime(r.t1_entrada)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontWeight: 600 }}>{fmtTime(r.t1_entrada)}</Typography>
                                                            )}
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: .2 }}>
                                                                {typeof r.t1_entrada_dist_m === "number" ? `${r.t1_entrada_dist_m} m` : "—"}
                                                            </Typography>
                                                        </>
                                                    ) : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {r.t1_salida ? (
                                                        <>
                                                            {t1OutLink ? (
                                                                <Typography component="a" href={t1OutLink} target="_blank" rel="noopener noreferrer"
                                                                    sx={{ color: "#1976d2", textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                                                                    {fmtTime(r.t1_salida)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontWeight: 600 }}>{fmtTime(r.t1_salida)}</Typography>
                                                            )}
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: .2 }}>
                                                                {typeof r.t1_salida_dist_m === "number" ? `${r.t1_salida_dist_m} m` : "—"}
                                                            </Typography>
                                                        </>
                                                    ) : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {typeof r.t1_min === "number" && r.t1_min > 0
                                                        ? (String(Math.floor(r.t1_min / 60)).padStart(2, "0") + ":" + String(r.t1_min % 60).padStart(2, "0"))
                                                        : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap", display: { xs: "none", md: "table-cell" } }}>
                                                    {r.t2_entrada ? (
                                                        <>
                                                            {t2InLink ? (
                                                                <Typography component="a" href={t2InLink} target="_blank" rel="noopener noreferrer"
                                                                    sx={{ color: "#1976d2", textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                                                                    {fmtTime(r.t2_entrada)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontWeight: 600 }}>{fmtTime(r.t2_entrada)}</Typography>
                                                            )}
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: .2 }}>
                                                                {typeof r.t2_entrada_dist_m === "number" ? `${r.t2_entrada_dist_m} m` : "—"}
                                                            </Typography>
                                                        </>
                                                    ) : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap", display: { xs: "none", md: "table-cell" } }}>
                                                    {r.t2_salida ? (
                                                        <>
                                                            {t2OutLink ? (
                                                                <Typography component="a" href={t2OutLink} target="_blank" rel="noopener noreferrer"
                                                                    sx={{ color: "#1976d2", textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } }}>
                                                                    {fmtTime(r.t2_salida)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontWeight: 600 }}>{fmtTime(r.t2_salida)}</Typography>
                                                            )}
                                                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: .2 }}>
                                                                {typeof r.t2_salida_dist_m === "number" ? `${r.t2_salida_dist_m} m` : "—"}
                                                            </Typography>
                                                        </>
                                                    ) : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap", display: { xs: "none", md: "table-cell" } }}>
                                                    {typeof r.t2_min === "number" && r.t2_min > 0
                                                        ? (String(Math.floor(r.t2_min / 60)).padStart(2, "0") + ":" + String(r.t2_min % 60).padStart(2, "0"))
                                                        : "—"}
                                                </TableCell>

                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                    {typeof r.total_min === "number" && r.total_min > 0
                                                        ? (String(Math.floor(r.total_min / 60)).padStart(2, "0") + ":" + String(r.total_min % 60).padStart(2, "0"))
                                                        : "—"}
                                                </TableCell>

                                                <TableCell sx={{ minWidth: { xs: 220, sm: 280 } }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        minRows={1}
                                                        maxRows={4}
                                                        value={r._obsDraft ?? r.observacion ?? ""}
                                                        onChange={(e) => r.dni && onDraftChange(r.dni, r.legajo, e.target.value, date)}
                                                        onBlur={(e) => r.dni && onBlurAutoSave(r.dni, e.target.value, date)}
                                                        placeholder={r.dni ? "Agregar observación del día…" : "Sin DNI — no se puede registrar"}
                                                        disabled={!r.dni}
                                                        InputProps={{ sx: { "& .MuiInputBase-input": { py: 0.5 } } }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                                No hay datos para mostrar.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, py: 1 }}>
                        <Button onClick={onClose} variant="outlined">Cerrar</Button>
                    </DialogActions>
                </Dialog>
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullScreen]);

    const ModalAusentesEmpresa = ({ open, onClose, rows, date }) => {
        const title = `Empresa (todas) — Ausentes — ${formatLongDateTitle(date)}${isToday(date) ? " (hoy)" : ""}`;
        return (
            <Dialog open={open} onClose={onClose} fullWidth fullScreen={fullScreen} maxWidth="lg" keepMounted
                PaperProps={{ sx: { width: "100%", height: fullScreen ? "100vh" : "80vh", m: 0, borderRadius: fullScreen ? 0 : 3 } }}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ px: 1, py: 1, overflow: "auto" }}>
                        <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 100 }}>ID Sucursal</TableCell>
                                    <TableCell sx={{ width: 220 }}>Sucursal</TableCell>
                                    <TableCell sx={{ width: 260 }}>Apellido, Nombre</TableCell>
                                    <TableCell sx={{ width: 160 }}>Puesto</TableCell>
                                    <TableCell>Observaciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={`emp-abs-${i}`} hover>
                                        <TableCell>{r.sucursal_id}</TableCell>
                                        <TableCell>{r.sucursal_nombre}</TableCell>
                                        <TableCell>{r.apellido_nombre}</TableCell>
                                        <TableCell>{r.puesto || "—"}</TableCell>
                                        <TableCell>{r.observacion || ""}</TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                            No hay ausentes para mostrar.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const onDraftChange = (dni, legajo, text, dateArg = selectedDate) => {
        setModalRows(prev => prev.map(r => (r.legajo === legajo ? { ...r, _obsDraft: text } : r)));
        scheduleAutoSave(dni, text, dateArg);
    };

    // 🔎 Lista a renderizar según switch "Solo con presentes"
    const displayedSucursales = useMemo(() => {
        if (!onlyWithPresent) return resumen;
        return resumen.filter(s => Number(s.presentes || 0) > 0);
    }, [resumen, onlyWithPresent]);

    return (
        <>
            <Navbar />
            <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Control horario
                    </Typography>

                    <Chip
                        size="small"
                        icon={<InfoOutlinedIcon />}
                        label="Total = legajos activos con controlhorario"
                        sx={{ bgcolor: "action.hover" }}
                    />

                    <Box sx={{ flexGrow: 1 }} />

                    <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
                        {formatLongDateTitle(selectedDate)}{isToday(selectedDate) ? " (hoy)" : ""}
                    </Typography>
                </Box>

                {/* Controles + Totales (responsive) */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                        flexWrap: "wrap",
                    }}
                >
                    {/* Izquierda: controles */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Button size="small" startIcon={<TodayIcon />} variant="outlined" onClick={setHoy}>HOY</Button>
                        <Button size="small" startIcon={<HistoryIcon />} variant="outlined" onClick={setAyer}>AYER</Button>

                        <TextField
                            size="small"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />

                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            color="success"
                            value={sortBy}
                            onChange={(_, v) => {
                                if (!v) return;
                                setSortBy(v);
                                localStorage.setItem("ph_sortBy", v);
                            }}
                            sx={{ ml: 0.5 }}
                        >
                            <ToggleButton value="id">ID</ToggleButton>
                            <ToggleButton value="nombre">NOMBRE</ToggleButton>
                        </ToggleButtonGroup>

                        {/* Switch de filtro: Solo con presentes */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onlyWithPresent}
                                    onChange={(e) => {
                                        const val = e.target.checked;
                                        setOnlyWithPresent(val);
                                        localStorage.setItem("ph_only_present", val ? "1" : "0");
                                    }}
                                    size="small"
                                />
                            }
                            label="Solo con presentes"
                            sx={{ ml: 1 }}
                        />

                        <Tooltip title="Descargar Marcaciones">
                            <span>
                                <IconButton
                                    aria-label="descargar"
                                    onClick={() => setDownloadModalOpen(true)}
                                    sx={{
                                        color: '#1976d2',
                                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                                        '&:hover': {
                                            color: '#1565c0',
                                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.5)',
                                            transform: 'translateY(-2px)',
                                            transition: 'all 0.2s ease'
                                        }
                                    }}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Derecha: Totales + MQTT (se pasa abajo en xs/sm si no entra) */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            ml: "auto",
                            mt: { xs: 1, sm: 0 },
                        }}
                    >
                        {/* Totales empresa solo si admin */}
                        {isAdmin && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "success.dark" }}>
                                    Totales
                                </Typography>

                                <Box sx={{ p: 0.4, px: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, textAlign: "center" }}>
                                    <Typography sx={{ fontFamily: '"Orbitron", monospace', fontWeight: 700, lineHeight: 1 }}>
                                        {empresaTotals.presentes}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>Presentes</Typography>
                                </Box>

                                <Box
                                    sx={{
                                        p: 0.4, px: 1, border: "1px solid", borderColor: "divider", borderRadius: 1,
                                        textAlign: "center", cursor: "pointer",
                                    }}
                                    onClick={async () => {
                                        try {
                                            setEmpresaModalRows([]);
                                            setEmpresaModalOpen(true);
                                            const rows = await api.getEmpresaAusentes(ensureYMD(selectedDate));
                                            setEmpresaModalRows(rows || []);
                                        } catch (e) {
                                            setEmpresaModalOpen(false);
                                            setSnack({ open: true, text: e.message || "No se pudo obtener ausentes empresa.", severity: "error" });
                                        }
                                    }}
                                    title="Ver ausentes de toda la empresa"
                                >
                                    <Typography sx={{ fontFamily: '"Orbitron", monospace', fontWeight: 700, lineHeight: 1, color: "error.main" }}>
                                        {empresaTotals.ausentes}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>Ausentes</Typography>
                                </Box>

                                <Box sx={{ p: 0.4, px: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, textAlign: "center" }}>
                                    <Typography sx={{ fontFamily: '"Orbitron", monospace', fontWeight: 700, lineHeight: 1 }}>
                                        {empresaTotals.total}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>Total</Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Chip MQTT */}
                        <Chip
                            size="small"
                            label={`MQTT: ${subscribed ? connectionStatus : "Paused"}`}
                            color={subscribed ? "success" : "default"}
                            variant={subscribed ? "filled" : "outlined"}
                            onClick={toggle}
                            sx={{ cursor: "pointer", "&:hover": { opacity: 0.85 } }}
                        />
                    </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                    Hacé clic en cada número para ver el detalle, entradas/salidas y cargar observación del día.
                </Typography>

                {/* Grid de sucursales (sin EMP) */}
                {loading ? (
                    <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                ) : (
                    <>
                        {displayedSucursales.length === 0 && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {onlyWithPresent ? "No hay sucursales con presentes." : "No tenés sucursales habilitadas para ver."}
                            </Alert>
                        )}
                        <Grid container spacing={1}>
                            {displayedSucursales.map((suc) => (
                                <Grid key={`${suc.sucursal_id}`} item xs={12} sm={6} md={4} lg={3}>
                                    <BranchCard
                                        suc={suc}
                                        idx={suc.sucursal_id}
                                        onOpen={(tipo) => abrirModal(suc, tipo)}
                                        onMenu={(key, s) => { setReportSucursal(s); setReportType(key); setReportOpen(true); }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {/* Modales */}
                <ModalLegajos
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    sucursal={modalSucursal}
                    tipo={modalTipo}
                    rows={modalRows}
                    date={modalDate}
                    onDraftChange={onDraftChange}
                    onBlurAutoSave={(dni, val, d) => scheduleAutoSave(dni, val, d)}
                />

                <ModalAusentesEmpresa
                    open={empresaModalOpen}
                    onClose={() => setEmpresaModalOpen(false)}
                    rows={empresaModalRows}
                    date={selectedDate}
                />

                {/* Modal Descargar Marcaciones */}
                <Dialog
                    open={downloadModalOpen}
                    onClose={() => setDownloadModalOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        Descargar Marcaciones
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                            <TextField
                                label="Fecha Desde"
                                type="date"
                                value={downloadFechaDesde}
                                onChange={(e) => setDownloadFechaDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="Fecha Hasta"
                                type="date"
                                value={downloadFechaHasta}
                                onChange={(e) => setDownloadFechaHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setDownloadModalOpen(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                api.downloadMarcaciones([1, 2], 'hipergas.txt', downloadFechaDesde, downloadFechaHasta);
                                setDownloadModalOpen(false);
                            }}
                        >
                            Hipergas
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                api.downloadMarcaciones([4], 'naturalgas.txt', downloadFechaDesde, downloadFechaHasta);
                                setDownloadModalOpen(false);
                            }}
                        >
                            Naturalgas
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Reporte mensual (solo ejemplo de fichadas) */}
                <Dialog
                    open={reportOpen}
                    onClose={() => setReportOpen(false)}
                    fullWidth
                    maxWidth="lg"
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogTitle sx={{ pr: 2, pb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontWeight: 700, flex: 1 }}>
                                {reportSucursal?.sucursal_nombre} — {
                                    reportType === "mensual_fichadas" ? "Fichadas mensuales" :
                                        reportType === "mensual_totales_empleado" ? "Totales por empleado (mes)" :
                                            reportType === "mensual_ausentismo" ? "Ausentismo (mes)" :
                                                reportType === "tendencias" ? "Tendencias" : "Reportes"
                                }
                            </Typography>
                            {reportType === "mensual_fichadas" && (
                                <TextField
                                    type="month"
                                    size="small"
                                    value={reportMonth}
                                    onChange={(e) => setReportMonth(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 190 }}
                                />
                            )}
                            <Button variant="outlined" onClick={() => setReportOpen(false)}>
                                Cerrar
                            </Button>
                        </Box>
                    </DialogTitle>

                    <DialogContent dividers sx={{ p: 0 }}>
                        {reportType === "mensual_fichadas" ? (
                            <TableContainer sx={{ maxHeight: fullScreen ? "calc(100vh - 160px)" : "60vh", overflow: "auto" }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 600 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Día</TableCell>
                                            <TableCell align="right">Presentes</TableCell>
                                            <TableCell align="right">Ausentes</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="right">% Asistencia</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportRows.map((r) => (
                                            <TableRow
                                                key={r.fecha}
                                                hover
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    const ymd = ensureYMD(r.fecha);
                                                    abrirModal(reportSucursal, "presentes", ymd);
                                                }}
                                            >
                                                <TableCell>{new Date(r.fecha).getDate()}</TableCell>
                                                <TableCell align="right">{r.presentes}</TableCell>
                                                <TableCell align="right">{r.ausentes}</TableCell>
                                                <TableCell align="right">{r.total}</TableCell>
                                                <TableCell align="right">
                                                    {typeof r.pct_asistencia === "number" ? r.pct_asistencia.toFixed(1) : "—"}%
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {reportRows.length === 0 && (
                                            <TableRow><TableCell colSpan={5} align="center">Sin datos</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Alert severity="info">Pendiente de backend.</Alert>
                        )}
                    </DialogContent>
                </Dialog>

                <Snackbar
                    open={snack.open}
                    autoHideDuration={3000}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        severity={snack.severity}
                        variant="filled"
                        onClose={() => setSnack((s) => ({ ...s, open: false }))}
                        sx={{ width: "100%" }}
                    >
                        {snack.text}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
}
